import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import Select from 'react-select';
import "react-datepicker/dist/react-datepicker.css";

import { UserAuth } from '../auth/Auth';
import { PrivateAxios } from '../../environment/AxiosInstance';
import { ErrorMessage, SuccessMessage } from '../../environment/ToastMessage';

function AddMiniProjectModal({ show, handleClose, onSuccess }) {
  const { User, MatchPermission } = UserAuth();
  
  // ================== ALL HOOKS MUST BE AT THE TOP ==================
  const [projectName, setProjectName] = useState("");
  const [projectHeads, setProjectHeads] = useState([]);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [isDarkTheme, setIsDarkTheme] = useState(false);

  const [startDate, endDate] = dateRange;

  // Theme Observer
  useEffect(() => {
    const checkTheme = () => {
      setIsDarkTheme(document.body.classList.contains("dark-theme"));
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // ================== PERMISSION CHECK AFTER ALL HOOKS ==================
  // Only allow users with Add Project permission
  if (!MatchPermission(['Add Project', 'Project All'])) {
    return null;
  }

  const handleCreate = async () => {
    if (!MatchPermission(['Add Project', 'Project All'])) {
      ErrorMessage("You don't have permission to add projects");
      return;
    }

    if (!projectName || !projectHeads.length) {
      alert("Please fill all required fields!");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        project_name: projectName,
        description,
        start_date: startDate ? startDate.toISOString() : null,
        end_date: endDate ? endDate.toISOString() : null,
        project_head_ids: projectHeads.map(u => u.id),
      };

      const res = await PrivateAxios.post("/project/create", payload);

      if (res.data.success) {
        SuccessMessage("Project created successfully!");
        // reset
        setProjectName("");
        setProjectHeads([]);
        setDescription("");
        setDateRange([null, null]);
        handleClose();

        if (onSuccess) onSuccess();
      } else {
        ErrorMessage(res.data.message || "Failed to create project");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        ErrorMessage("Session expired. Please log in again.");
      } else {
        ErrorMessage(err.response?.data?.message || "Server error!");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal id="AddProjectModal" show={show} onHide={handleClose} backdrop="static" centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="gth-modal-title">Add Project</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pb-1">
        <div className="row">
          {/* Project Name */}
          <div className="col-lg-12">
            <div className="form-group">
              <label className="form-label">Project Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Project Name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
              />
            </div>
          </div>

          {/* Project Head */}
          <div className="col-lg-6">
            <div className="form-group">
              <label className="form-label">Project Head</label>
              <div className="custom-select-wrap">
                <Select
                  isMulti
                  options={User}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  value={projectHeads}
                  onChange={(selected) => setProjectHeads(selected)}
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary25: '#ddddff',
                      primary: '#6161ff',
                    },
                  })}
                  styles={{
                    menu: (base) => ({
                      ...base,
                      backgroundColor: isDarkTheme ? "#06162f" : "#ffffff",
                      color: isDarkTheme ? "#e0e0e0" : "#000000",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: isDarkTheme ? "#e0e0e0" : "#000000",
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isFocused
                        ? isDarkTheme
                          ? "#112a46"
                          : "#ddddff"
                        : "transparent",
                      color: isDarkTheme ? "#e0e0e0" : "#000000",
                    }),
                  }}
                />
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="col-lg-6">
            <div className="form-group">
              <label className="form-label">Project TimeLine</label>
              <div className="exp-datepicker-cont">
                <span className="cal-icon">
                  <i className="fas fa-calendar-alt"></i>
                </span>
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => setDateRange(update)}
                  isClearable={false}
                  placeholderText={startDate && endDate ? "" : "Select Date"}
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="col-lg-12">
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              ></textarea>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="gth-blue-light-bg">
        <button type="button" className="btn" onClick={handleClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-exp-green"
          onClick={handleCreate}
          disabled={loading}
        >
          {loading ? "Creating..." : "Create"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddMiniProjectModal;
